import { useQuery } from '@tanstack/react-query';
import { minutesToMilliseconds } from 'date-fns';

import { api } from '@api';
import { authStore } from '@stores/authStore';
import { useContractId } from '@stores/contractStore';

/** Utility for obtaining Incidents data for a given/currently selected contract */
export const useIncidents = (props?: {
  /** Contract ID */
  id?: string;
  disabled: boolean;
}) => {
  const contractIdFromStore = useContractId();

  const contractId = props?.id ?? contractIdFromStore;

  const isEnabled =
    !props?.disabled && !!contractId && authStore.isAuthenticated;

  const {
    data: incidentsData,
    isLoading,
    ...rest
  } = useQuery({
    queryKey: ['incidents', contractId],
    queryFn: () => {
      if (!contractId) return;

      return api.incidents.fetchIncidents(contractId);
    },
    /* check authStore.isAuthenticated state on re-renders but don't trigger
     * useQuery on authStore.isAuthenticated change (since it would require wrapping
     * the hook inside an observer somehow). This is because useQuery hooks are only
     * invoked when the user is accessing an authenticated route, i.e. has authenticated
     * already. However, when logging-out, authStore.isAuthenticated state changes before
     * unmounting the authenticated routes and, therefore, up-to-date authentications
     * state must be checked to avoid illegal API calls on log-out */
    enabled: isEnabled,
    staleTime: minutesToMilliseconds(1),
    refetchOnWindowFocus: true,
    refetchOnMount: 'always',
  });

  return {
    incidents: incidentsData?.data.contactIncidents,
    // if query is disabled `isLoading` is true by default
    isLoading: isEnabled && isLoading,
    ...rest,
  };
};
